import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getPropuestaDetail, listTags } from '@/api/clientes';
import { usePropuestaSocket } from '@/hooks/usePropuestaSocket';
import { useUIStore } from '@/store/uiStore';
import { tagMap } from '@/lib/propuesta-selectors';
import { Header } from '@/components/Header';
import { LenteToolbar } from '@/components/LenteToolbar';
import { ScopeChip } from '@/components/ScopeChip';
import { EntregasView } from '@/components/EntregasView';
import { IniciativasView } from '@/components/IniciativasView';
import { HitosView } from '@/components/HitosView';
import { CompromisosView } from '@/components/CompromisosView';
import styles from './FacilityPage.module.css';
import detailStyles from '../pages/pages.module.css';

export function PropuestaPage() {
  const { clienteSlug, facilitySlug, propuestaId } = useParams<{
    clienteSlug: string;
    facilitySlug: string;
    propuestaId: string;
  }>();
  const qc = useQueryClient();
  const topTab = useUIStore((s) => s.topTab);
  const bottomTab = useUIStore((s) => s.bottomTab);
  const setTopTab = useUIStore((s) => s.setTopTab);
  const setBottomTab = useUIStore((s) => s.setBottomTab);
  const scope = useUIStore((s) => s.scope);
  const row2Ref = useRef<HTMLDivElement | null>(null);

  const queryKey = ['propuesta-detail', propuestaId];
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => getPropuestaDetail(propuestaId!),
    enabled: !!propuestaId,
  });

  const { data: allTags } = useQuery({
    queryKey: ['tags'],
    queryFn: listTags,
    staleTime: 5 * 60 * 1000,
  });

  // WebSocket: cuando otro colega cambia un hito, refresh
  usePropuestaSocket(data?.propuesta.id, {
    onHitoChanged: () => qc.invalidateQueries({ queryKey }),
  });

  // Auto-scroll suave a Row 2 al fijar scope (R1)
  useEffect(() => {
    if (scope.type && scope.id) {
      requestAnimationFrame(() => {
        row2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [scope.type, scope.id]);

  /**
   * R5: deep-link a un compromiso específico.
   * Si la URL trae `#compromiso-<id>` (viene del listado consolidado por cliente),
   * cambiamos a la tab "Compromisos" y hacemos scroll al elemento.
   */
  const location = useLocation();
  useEffect(() => {
    if (!data) return;
    const match = location.hash.match(/^#compromiso-(.+)$/);
    if (!match) return;
    const compromisoId = match[1];
    setBottomTab('compromisos');
    // Esperamos al render de CompromisosView
    const t = setTimeout(() => {
      const el = document.getElementById(`compromiso-${compromisoId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Highlight temporal
        el.classList.add('hash-highlight');
        setTimeout(() => el.classList.remove('hash-highlight'), 2400);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, data?.propuesta.id]);

  const tagsById = useMemo(() => {
    if (!allTags) return new Map<string, { nombre: string; color: string }>();
    const m = new Map<string, { nombre: string; color: string }>();
    for (const t of allTags) m.set(t.id, { nombre: t.nombre, color: t.color });
    return m;
  }, [allTags]);

  if (isLoading) {
    return (
      <>
        <Header
          breadcrumbs={[{ label: 'Inicio', to: '/' }, { label: '…' }]}
          title="Cargando propuesta…"
        />
        <div className={`container ${detailStyles.body}`}>
          <div className="empty">Cargando…</div>
        </div>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <Header breadcrumbs={[{ label: 'Inicio', to: '/' }]} title="No encontrada" />
        <div className={`container ${detailStyles.body}`}>
          <div className="empty">No se pudo cargar la propuesta.</div>
        </div>
      </>
    );
  }

  const { cliente, facility, propuesta } = data;

  return (
    <>
      <Header
        breadcrumbs={[
          { label: 'Inicio', to: '/' },
          { label: cliente.nombre, to: `/clientes/${cliente.slug}` },
          { label: facility.nombre, to: `/clientes/${cliente.slug}/${facility.slug}` },
          { label: propuesta.codigo },
        ]}
        title={
          <>
            {propuesta.nombre}
            <span className={styles.clientPill}>
              <span className={styles.pillDot} style={{ background: cliente.color }} />
              {cliente.nombre} · {facility.nombre}
            </span>
          </>
        }
        subtitle={`Estado: ${propuesta.estado.replace('_', ' ')}${propuesta.notas ? ` · ${propuesta.notas}` : ''}`}
        meta={
          <>
            <span>{data.entregasValor.length} EVs</span>
            <span>{data.iniciativas.length} iniciativas</span>
            <span>{data.compromisos.length} compromisos</span>
            <span>{data.hitos.length} hitos</span>
          </>
        }
      />

      <div className={`container ${detailStyles.body}`}>
        {/* Row 1 — scope */}
        <div className={`${styles.tabs} ${styles.tabsRow1}`}>
          <button
            className={`${styles.tab} ${topTab === 'entregas' ? styles.active : ''}`}
            onClick={() => setTopTab('entregas')}
          >
            Entregas de Valor
          </button>
          <button
            className={`${styles.tab} ${topTab === 'iniciativas' ? styles.active : ''}`}
            onClick={() => setTopTab('iniciativas')}
          >
            Iniciativas
          </button>
        </div>

        <LenteToolbar />

        {topTab === 'entregas' && <EntregasView detail={data} tagsById={tagsById} />}
        {topTab === 'iniciativas' && <IniciativasView detail={data} tagsById={tagsById} />}

        <div ref={row2Ref} style={{ scrollMarginTop: 16 }}>
          <ScopeChip detail={data} />

          <div className={`${styles.tabs} ${styles.tabsRow2}`}>
            <button
              className={`${styles.tab} ${bottomTab === 'hitos' ? styles.active : ''}`}
              onClick={() => setBottomTab('hitos')}
            >
              Hitos
            </button>
            <button
              className={`${styles.tab} ${bottomTab === 'compromisos' ? styles.active : ''}`}
              onClick={() => setBottomTab('compromisos')}
            >
              Compromisos
            </button>
          </div>

          {bottomTab === 'hitos' && <HitosView detail={data} />}
          {bottomTab === 'compromisos' && <CompromisosView detail={data} tagsById={tagsById} />}
        </div>
      </div>
    </>
  );
}
